import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { createSupplier, SUPPLIER_TYPES } from '@/lib/suppliers/service';
import { Field } from '@/components/common/F1States';

const empty = { legalName: '', tradingName: '', croNumber: '', vatNumber: '', categories: '', types: [] };

export const NewSupplierDialog = ({ open, onOpenChange, organisationId, onCreated }) => {
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const navigate = useNavigate();
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const toggle = (t) => setF((s) => ({ ...s, types: s.types.includes(t) ? s.types.filter((x) => x !== t) : [...s.types, t] }));

  const submit = async (e) => {
    e.preventDefault(); setErr(''); setBusy(true);
    try {
      const res = await createSupplier({
        organisationId, legalName: f.legalName.trim(), tradingName: f.tradingName.trim(), croNumber: f.croNumber.trim(), vatNumber: f.vatNumber.trim(),
        supplierTypes: f.types, categories: f.categories.split(',').map((s) => s.trim()).filter(Boolean),
      });
      toast.success(res.linked_existing_entity ? `Linked existing company "${res.legal_name}" (matched on ${res.matched_on})` : `Supplier "${res.legal_name}" created`);
      setF(empty); onOpenChange(false); onCreated?.(); navigate(`/suppliers/${res.supplier_id}`);
    } catch (ex) { setErr(ex.message || 'Could not create supplier'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="supplier-new-dialog" className="sm:max-w-lg">
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>New supplier</DialogTitle>
            <DialogDescription>Minimal entry: legal name, roles and categories. Account terms, contacts and documents are added on the supplier record. Duplicates are detected on legal name, CRO and VAT number.</DialogDescription>
          </DialogHeader>
          <Field label="Legal company name *"><Input data-testid="supplier-legal-name" required value={f.legalName} onChange={set('legalName')} placeholder="e.g. Chadwicks Ltd" /></Field>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Trading name"><Input data-testid="supplier-trading-name" value={f.tradingName} onChange={set('tradingName')} /></Field>
            <Field label="CRO number"><Input data-testid="supplier-cro" value={f.croNumber} onChange={set('croNumber')} /></Field>
            <Field label="VAT number"><Input data-testid="supplier-vat" value={f.vatNumber} onChange={set('vatNumber')} placeholder="IE1234567T" /></Field>
          </div>
          <Field label="Supplier roles">
            <div className="grid grid-cols-2 gap-2">
              {SUPPLIER_TYPES.map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 text-sm"><Checkbox data-testid={`supplier-type-${k}`} checked={f.types.includes(k)} onCheckedChange={() => toggle(k)} /> {label}</label>
              ))}
            </div>
          </Field>
          <Field label="Purchasing categories" hint="Comma-separated, e.g. Timber, Roofing, Plumbing"><Input data-testid="supplier-categories" value={f.categories} onChange={set('categories')} /></Field>
          {err && <div data-testid="supplier-new-error" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{err}</div>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" data-testid="supplier-new-submit" disabled={busy || !f.legalName.trim()} className="bg-bronze hover:bg-bronze-hover text-white">{busy ? <Loader2 size={14} className="animate-spin" /> : 'Create supplier'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
